import { ShieldCheck, FileText, AlertTriangle } from 'lucide-react';
import SEO from '../components/SEO';
import { COMPLIANCE_DISCLOSURE_TEXT } from '../constants/site';

const Disclaimer = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Disclaimer | Important Legal Information"
        description={`${COMPLIANCE_DISCLOSURE_TEXT} Interest rate and comparison rate warnings apply.`}
        keywords="Flash Funding disclaimer, credit representative, Australian credit licence, interest rate warning, comparison rate warning, financial disclaimer"
        canonical="/disclaimer"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight leading-none mb-4">
              Important<br />
              <span className="gold-gradient-text">Disclaimer</span>
            </h1>
            <p className="font-body text-lg text-white/70">
              Important information you should know about Flash Funding and our services.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Credit Representative Info */}
          <div className="bg-navy-700 border border-gold-500/20 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={24} className="text-gold-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                  Authorised Credit Representative
                </h2>
              </div>
            </div>
            <p className="font-body text-white/80 leading-relaxed">
              {COMPLIANCE_DISCLOSURE_TEXT}
            </p>
          </div>

          {/* General Disclaimer */}
          <div className="bg-navy-700 border border-white/5 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                <FileText size={24} className="text-gold-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                  General Information
                </h2>
              </div>
            </div>
            <div className="space-y-4 font-body text-white/70 leading-relaxed">
              <p>
                All applications are subject to the credit provider's credit assessment and loan eligibility criteria. Terms, conditions, fees and charges apply.
              </p>
              <p>
                Information provided is factual information only, and is not intended to imply any recommendation about any financial product(s) or constitute tax advice. If you require financial or tax advice you should consult a licensed financial or tax adviser.
              </p>
            </div>
          </div>

          {/* Interest Rate Disclaimer */}
          <div className="bg-navy-700 border border-white/5 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={24} className="text-gold-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                  Interest Rates*
                </h2>
              </div>
            </div>
            <p className="font-body text-white/70 leading-relaxed">
              Your interest rate may be different and is based on a number of factors, including your credit history, the information you provide and our assessment of your application. We'll confirm your interest rate upon submission of your application. Rates, repayments and fees are estimates and are subject to change at any time.
            </p>
          </div>

          {/* Comparison Rate Warning */}
          <div className="bg-navy-700 border border-red-500/30 p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={24} className="text-red-400" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                  Comparison Rate Warning*
                </h2>
              </div>
            </div>
            <div className="space-y-4 font-body text-white/70 leading-relaxed">
              <p>
                The comparison rate is based on a $30,000 secured loan over 5 years.
              </p>
              <p className="text-red-400 font-semibold">
                WARNING: This comparison rate applies only to the example or examples given. Different amounts and terms will result in different comparison rates.
              </p>
              <p>
                Costs such as redraw fees or early repayment fees, and cost savings such as fee waivers, are not included in the comparison rate but may influence the cost of the loan. Eligibility criteria, terms and conditions apply.
              </p>
            </div>
          </div>

          {/* Last Updated */}
          <p className="text-white/40 font-body text-sm text-center mt-12">
            Last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;
